import React, { useState, useRef, useEffect, Fragment } from 'react'
import {
	Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
	IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined'
import RefreshIcon from '@material-ui/icons/Refresh'
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined'
import GetAppIcon from '@material-ui/icons/GetApp'
import { Link } from 'react-router-dom'
import { connect, useSelector } from "react-redux"
import './index.css'
import HttpService from '../../../services/httpService'
import { setLoading, setSnackInfo } from '../../../redux/actions/appAction'

// key = field name expected by the OptimizationFiles schema. The pasting list and
// the pasting list BOM are not uploaded: the server generates both out of the
// patterns & summaries PDF.
const FILE_FIELDS = [
	{ key: 'excelFileLink', label: 'Tracking Sheet', short: 'Tracking', hint: 'Optimization tracking sheet', accept: '.xlsx, .xls, .csv' },
	{ key: 'patternFileLink', label: 'Patterns & Summaries', short: 'Patterns', hint: 'Pattern / summary export from optimizer', accept: '.pdf' },
	{ key: 'imosFile', label: 'IMOS File', short: 'IMOS', hint: 'IMOS part list export', accept: '.xlsx, .xls, .csv' },
	{ key: 'sawFileLink', label: 'Saw File 1', short: 'Saw 1', hint: 'First saw cutting file', accept: '.saw' },
	{ key: 'sawFile2Link', label: 'Saw File 2', short: 'Saw 2', hint: 'Second saw cutting file', accept: '.saw' }
]

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

// workflow steps, not the file list : step 1 is the updateOptimisationFiles call
const STAGES = [
	{ key: 'optimisation', label: 'Create Optimization', short: 'Optimization' },
	{ key: 'bomSegregation', label: 'BOM Segregation', short: 'BOM' },
	{ key: 'bomFiles', label: 'BOM Files Created', short: 'BOMs Created' },
	{ key: 'bomUpload', label: 'BOM Auto Upload & Approve', short: 'BOM Upload' }
]

// idle | running | success | error, one entry per workflow step
const INITIAL_STAGE_STATE = STAGES.reduce((acc, stage) => {
	acc[stage.key] = { status: 'idle', message: '' }
	return acc
}, {})

const INITIAL_FILE_STATE = FILE_FIELDS.reduce((acc, field) => {
	acc[field.key] = { fileName: '', link: '', status: 'idle' }	// idle | uploading | success | error
	return acc
}, {})

const Index = (props) => {
	const { userDetails } = useSelector(state => state.user)

	const [uploadedFiles, setUploadedFiles] = useState(INITIAL_FILE_STATE)
	const [creating, setCreating] = useState(false)					// loader for the upload section only
	const [createdOptimisation, setCreatedOptimisation] = useState(null)	// api response once success: true

	const uploadedCount = FILE_FIELDS.filter(field => uploadedFiles[field.key].status === 'success').length
	const allFilesUploaded = uploadedCount === FILE_FIELDS.length
	const [projectSummary, setProjectSummary] = useState([]);
	const [csvData, setCsvData] = useState([]);
	const [uploadedId, setUploadedId] = useState("");
	const [bomsCreated, setBomsCreated] = useState([]);
	const [imosData, setImosData] = useState([]);
	const [bomData, setBomData] = useState([]);
	const [selectedProjectIds, setSelectedProjectIds] = useState([]);
	const [busyText, setBusyText] = useState('')					// message inside the section loader
	const [bomDialogOpen, setBomDialogOpen] = useState(false)
	const [viewBomProject, setViewBomProject] = useState('')		// project whose bom items are expanded
	const [uploadResults, setUploadResults] = useState([])			// per project auto upload outcome
	const [autoApproveStatus, setAutoApproveStatus] = useState([])	// per project auto approve outcome
	const [optimizationName, setOptimizationName] = useState([]);
	const [stageState, setStageState] = useState(INITIAL_STAGE_STATE)	// per step status
	const [failedStage, setFailedStage] = useState('')					// step to resume from
	const [segregatedFileLink, setSegregatedFileLink] = useState('')	// s3 copy of the segregated bom, saved after step 4
	const pipeline = useRef({})											// data handed between steps
	const [availableNumbers, setAvailableNumbers] = useState([])
	const [selectedNumber, setSelectedNumber] = useState(null)
	const [reserving, setReserving] = useState(false)

	const fetchAvailableNumbers = async () => {
		try {
			const res = await HttpService.getAvailableOptimizationNumbers(userDetails?._id)
			setAvailableNumbers(res?.data?.numbers || [])
			if (res?.data?.reserved) setSelectedNumber(res.data.reserved)
		} catch (err) {
			props.setSnackInfo(readError(err, 'Could not load optimization numbers'), "error")
		}
	}

	useEffect(() => {
		if (selectedNumber) return
		fetchAvailableNumbers()
		const timer = setInterval(fetchAvailableNumbers, 15000)
		return () => clearInterval(timer)
	}, [selectedNumber])

	const handleSelectNumber = async (item) => {
		setReserving(true)
		try {
			const res = await HttpService.reserveOptimizationNumber({ number: item.number, userId: userDetails._id })
			setSelectedNumber(res.data)
			props.setSnackInfo(`${res.data.optimizationNumber} selected`, "success")
		} catch (err) {
			props.setSnackInfo(readError(err, 'Could not select the optimization number'), "error")
			await fetchAvailableNumbers()
		}
		setReserving(false)
	}


	const handleChooseFile = async (event, key) => {
		const input = event.target					// keep the node, event may be recycled after await
		const file = input.files && input.files[0]
		if (!file) return

		const field = FILE_FIELDS.find(el => el.key === key)
		setUploadedFiles(prev => ({ ...prev, [key]: { fileName: file.name, link: '', status: 'uploading' } }))
		try {
			const formData = new FormData()
			formData.append('file', file)
			formData.append('customerName', 'OptimisationFiles')

			const result = await HttpService.wdFileUpload(formData)

			if (result.status === 200 && result.data) {
				// keep the uploaded file url (string) in state for the create payload
				setUploadedFiles(prev => ({ ...prev, [key]: { fileName: file.name, link: result.data, status: 'success' } }))
				props.setSnackInfo(`${field.label} Uploaded Successfully`, "success")
			} else {
				setUploadedFiles(prev => ({ ...prev, [key]: { fileName: file.name, link: '', status: 'error' } }))
				props.setSnackInfo(`${field.label} Upload Failed`, "error")
			}
		}
		catch (error) {
			console.error(`[optimisation upload] ${field.label} failed`, error)
			setUploadedFiles(prev => ({ ...prev, [key]: { fileName: file.name, link: '', status: 'error' } }))
			props.setSnackInfo(`${field.label} Upload Failed`, "error")
		}
		finally {
			input.value = ''						// allow re-picking the same file
		}
	}

	const resetForm = async () => {
		if (selectedNumber && !createdOptimisation) {
			try {
				await HttpService.releaseOptimizationNumber({ userId: userDetails._id })
			} catch (err) {
				props.setSnackInfo(readError(err, 'Could not release the optimization number'), "error")
				return
			}
		}
		setSelectedNumber(null)
		setUploadedFiles(INITIAL_FILE_STATE)
		setCreatedOptimisation(null)
		setStageState(INITIAL_STAGE_STATE)
		setFailedStage('')
		setBomsCreated([])
		setUploadResults([])
		setProjectSummary([])
		setCsvData([])
		setImosData([])
		setBomData([])
		setUploadedId("")
		setSelectedProjectIds([])
		setOptimizationName([])
		setViewBomProject('')
		setSegregatedFileLink('')
		pipeline.current = {}
	}

	const markStage = (key, status, message = '') => {
		setStageState(prev => ({ ...prev, [key]: { status, message } }))
	}

	// one step failing carries its stage key out to the runner
	const stepError = (stage, message) => Object.assign(new Error(message), { stage })

	const readError = (err, fallback) => err?.response?.data?.message || err?.message || fallback

	// ---------- step 1 : create the optimization ----------
	const stepCreateOptimisation = async () => {
		markStage('optimisation', 'running')
		setBusyText('Step 1 of 4 — Creating optimization…')

		const data = {
			...FILE_FIELDS.reduce((acc, field) => {
				acc[field.key] = uploadedFiles[field.key].link
				return acc
			}, {}),
			// original file names, keyed by the matching link field
			fileNames: FILE_FIELDS.reduce((acc, field) => {
				acc[field.key] = uploadedFiles[field.key].fileName
				return acc
			}, {}),
			uploadedBy: userDetails._id,
			optimizationNumber: selectedNumber?.number,
			procurementNumber: 1,
			materialTransferNumber: 1
		}

		let response
		try {
			response = await HttpService.updateOptimisationFiles(data)
		} catch (err) {
			throw stepError('optimisation', readError(err, 'Could not create the optimization'))
		}

		if (!(response.status === 200 && response.data && response.data.success)) {
			throw stepError('optimisation', response.data?.message || 'Could not create the optimization')
		}

		const savedOptimisation = response.data.newOptimisationFile
		setCreatedOptimisation(response.data)
		setOptimizationName(savedOptimisation ? [savedOptimisation] : [])
		markStage('optimisation', 'success', response.data.optimizationNumber)

		return { response: response.data, savedOptimisation, projectIds: savedOptimisation?.projects || [] }
	}

	// ---------- step 2 : segregate the bom ----------
	const stepSegregate = async (optimisation) => {
		markStage('bomSegregation', 'running')
		setBusyText('Step 2 of 4 — Segregating BOM…')

		const payload = {
			projectIds: optimisation.projectIds,
			optimizationName: optimisation.savedOptimisation?.optimizationNumber,
			materialBomFile: optimisation.savedOptimisation?.pastingListBOMFile,
			optimizationBomFile: optimisation.savedOptimisation?.excelFileLink,
			userId: userDetails?._id,
			imosFile: optimisation.savedOptimisation ? [optimisation.savedOptimisation] : [],
		}
		setSelectedProjectIds(optimisation.projectIds)

		let res
		try {
			res = await HttpService.segregateHandleBom(payload)
		} catch (err) {
			throw stepError('bomSegregation', readError(err, 'BOM segregation failed'))
		}

		if (!res?.data?.success) throw stepError('bomSegregation', res?.data?.message || 'BOM segregation failed')

		const savedId = res?.data?.bomSegregationDBSaveId || ""
		const csvRows = res?.data?.projectResults || []
		setProjectSummary(res?.data?.data || [])
		setCsvData(csvRows)
		setUploadedId(savedId)
		setImosData(res?.data?.IMOSFileData || [])
		setBomData(res?.data?.bomData || [])
		markStage('bomSegregation', 'success', `${(res?.data?.data || []).length} project(s) segregated`)

		return { savedId, projectIds: optimisation.projectIds, csvRows }
	}

	// ---------- step 3 : project wise bom files ----------
	const stepCreateBomFiles = async (segregation) => {
		markStage('bomFiles', 'running')
		setBusyText('Step 3 of 4 — Creating BOM files…')

		let res
		try {
			res = await HttpService.segregatedBomFileCreation({
				selectedProjectId: segregation.projectIds,
				uploadedId: segregation.savedId
			})
		} catch (err) {
			throw stepError('bomFiles', readError(err, 'BOM file creation failed'))
		}

		if (!res?.data?.success) throw stepError('bomFiles', res?.data?.message || 'BOM file creation failed')

		const bomFiles = res?.data?.data || []
		setBomsCreated(bomFiles)
		markStage('bomFiles', 'success', `${bomFiles.length} BOM file(s)`)

		return bomFiles
	}

	// ---------- step 4 : auto upload and auto approve ----------
	const stepAutoUpload = async (context) => {
		markStage('bomUpload', 'running')
		setBusyText('Step 4 of 4 — Auto uploading and approving…')

		// the api reads imosFile[0].imosFile and falls back to imosFile[0]._id
		const savedOptimisation = context.savedOptimisation || optimizationName?.[0] || createdOptimisation?.newOptimisationFile
		const optimisationFiles = savedOptimisation ? [savedOptimisation] : []

		let res
		try {
			res = await HttpService.segregatedBomFileAutoUplaod({
				bomType: "Modular BOM",
				bomFiles: context.bomFiles,
				uploadedId: context.savedId,
				projectIds: context.projectIds,
				userId: userDetails?._id,
				imosFile: optimisationFiles,
				optimizationId: optimisationFiles?.[0]?._id || createdOptimisation?._id,
			})
		} catch (err) {
			throw stepError('bomUpload', readError(err, 'Auto upload failed'))
		}

		// success is only true when every project uploaded, so keep the rows either way
		const results = res?.data?.uploadResults || []
		setUploadResults(results)
		setAutoApproveStatus(res?.data?.autoApproveResult?.projectStatus || [])

		if (res?.data?.autoApproveResult === null) {
			props.setSnackInfo("Please check whether the IMOS file is correct and in the proper format.", "warning")
		} else if (res?.data?.autoApproveResult?.message) {
			props.setSnackInfo(res.data.autoApproveResult.message, "success")
		}

		if (!res?.data?.success) {
			const failed = results.filter(row => !row.uploaded).length
			throw stepError('bomUpload', res?.data?.message || `${failed} project(s) failed to upload`)
		}

		markStage('bomUpload', 'success', `${results.length} project(s) uploaded`)
		return results
	}

	// ---------- after step 4 : keep the segregated bom on s3 ----------
	const saveSegregatedExcel = async (context) => {
		const savedOptimisation = context.savedOptimisation || optimizationName?.[0] || createdOptimisation?.newOptimisationFile
		const rows = context.csvRows?.length ? context.csvRows : csvData

		if (!rows?.length || !savedOptimisation?._id) return

		setBusyText('Saving the segregated BOM file\u2026')
		try {
			// the same excel the download button hands over
			const excel = await HttpService.downloadSegregatedBomExcel({
				csvData: rows,
				optimizationName: savedOptimisation.optimizationNumber,
				userId: userDetails?._id
			})

			// uploaded through the same file upload every other optimisation file goes through
			const formData = new FormData()
			formData.append('file', new Blob([excel.data], { type: XLSX_MIME }), `${savedOptimisation.optimizationNumber || 'BOM_Segregation'}.xlsx`)
			formData.append('customerName', 'OptimisationFiles')

			const upload = await HttpService.wdFileUpload(formData)
			if (!(upload.status === 200 && upload.data)) throw new Error('Segregated BOM file upload failed')

			const res = await HttpService.saveSegregatedBomExcel({
				optimizationId: savedOptimisation._id,
				fileLink: upload.data
			})

			if (res?.data?.success) setSegregatedFileLink(upload.data)
			else props.setSnackInfo(res?.data?.message || 'Could not save the segregated BOM file', "warning")
		}
		catch (err) {
			console.error('[optimisation upload] segregated BOM file save failed', err)
			props.setSnackInfo(readError(err, 'Could not save the segregated BOM file'), "warning")
		}
	}

	// runs steps in order and can resume from the one that failed
	const runPipeline = async (startFrom = 'optimisation') => {
		const order = STAGES.map(stage => stage.key)
		const from = order.indexOf(startFrom) === -1 ? 0 : order.indexOf(startFrom)

		setCreating(true)
		setFailedStage('')

		try {
			if (from <= 0) pipeline.current.optimisation = await stepCreateOptimisation()
			if (from <= 1) pipeline.current.segregation = await stepSegregate(pipeline.current.optimisation)
			if (from <= 2) pipeline.current.bomFiles = await stepCreateBomFiles(pipeline.current.segregation)
			if (from <= 3) {
				await stepAutoUpload({
					savedOptimisation: pipeline.current.optimisation?.savedOptimisation,
					savedId: pipeline.current.segregation?.savedId,
					projectIds: pipeline.current.segregation?.projectIds,
					bomFiles: pipeline.current.bomFiles
				})
				// step 4 is done, so archive the segregated BOM against the optimization
				await saveSegregatedExcel({
					savedOptimisation: pipeline.current.optimisation?.savedOptimisation,
					csvRows: pipeline.current.segregation?.csvRows
				})
			}

			props.setSnackInfo(`Optimization ${pipeline.current.optimisation?.response?.optimizationNumber || ''} completed successfully`, "success")
		}
		catch (err) {
			const stage = err.stage || 'optimisation'
			markStage(stage, 'error', err.message)
			setFailedStage(stage)
			props.setSnackInfo(err.message, "error")
		}

		setCreating(false)
		setBusyText('')
	}

	const handleCreateBomSegregation = () => runPipeline('optimisation')

	const stageStatus = (key) => {
		const state = stageState[key]
		if (state.status === 'idle' && key === 'optimisation') return allFilesUploaded ? 'ready' : 'idle'
		if (state.status === 'running') return 'uploading'
		return state.status
	}

	const renderStageNode = (status, position) => {
		if (status === 'uploading') return <CircularProgress size={16} thickness={6} style={{ color: '#f47d44' }} />
		if (status === 'success') return <CheckCircleIcon style={{ fontSize: 20 }} />
		if (status === 'error') return <ErrorOutlineIcon style={{ fontSize: 20 }} />
		return position
	}

	const stageStatusText = (key, status) => {
		const message = stageState[key]?.message
		if (status === 'uploading') return 'In progress…'
		if (status === 'success') return message ? `Completed — ${message}` : 'Completed'
		if (status === 'error') return message ? `Failed — ${message}` : 'Failed'
		if (status === 'ready') return 'Ready to start'
		return 'Pending'
	}

	const renderFileStatus = (state) => {
		if (state.status === 'uploading') return <span className="of-pill of-pill-pending"><CircularProgress size={13} thickness={6} style={{ color: '#f47d44' }} /> Uploading</span>
		if (state.status === 'success') return <span className="of-pill of-pill-success"><CheckCircleIcon style={{ fontSize: 15 }} /> Uploaded</span>
		if (state.status === 'error') return <span className="of-pill of-pill-error"><ErrorOutlineIcon style={{ fontSize: 15 }} /> Failed &mdash; retry</span>
		return <span className="of-pill of-pill-idle"><span className="of-dot" /> Pending</span>
	}

	const uploadStatusFor = (projectNumber) => uploadResults.find(row => row.projectNumber === projectNumber)
	const approveStatusFor = (projectNumber) => autoApproveStatus.find(row => row.projectNumber === projectNumber)

	const completedStages = STAGES.filter(stage => stageState[stage.key].status === 'success').length
	const allStagesDone = completedStages === STAGES.length
	const bomUploadDone = stageState.bomUpload.status === 'success'


	const handleDownloadExcel = async () => {
		try {
			props.setLoading(true);

			const res = await HttpService.downloadSegregatedBomExcel({
			csvData,
			optimizationName:optimizationName?.[0]?.optimizationNumber,
			userId: userDetails?._id,
			});

			const blob = new Blob([res.data], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});

			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");

			link.href = url;
			link.setAttribute(
			"download",
			`${optimizationName?.[0]?.optimizationNumber || "BOM_Segregation"}.xlsx`
			);

			document.body.appendChild(link);
			link.click();
			link.remove();

		} catch (err) {
			props.setSnackInfo("Excel download failed", "error");
		}
		props.setLoading(false);
	};

	// Prefer the copy saved against the optimization, and rebuild it only if that save failed.
	const handleDownloadSegregatedBom = () => {
		if (segregatedFileLink) {
			window.open(segregatedFileLink)
			return
		}
		handleDownloadExcel()
	}

	return (
		<div className="of-page">
			<div className="of-card">
				{/* ---------- header ---------- */}
				<div className="of-header">
					<div className="of-header-main">
						<div className="of-header-icon"><CloudUploadOutlinedIcon /></div>
						<div>
							<h2>Unified Optimization &rarr; BOM &rarr; P&amp;L</h2>
							<p>Factory Team &bull; Create an optimization batch and segregate its BOM</p>
						</div>
					</div>
					<div className="of-header-badge">{uploadedCount} / {FILE_FIELDS.length} Files</div>
				</div>

				{/* ---------- stage-wise status ---------- */}
				<div className="of-stages">
					<div className="of-stages-track">
						{
							STAGES.map((stage, index) => {
								const status = stageStatus(stage.key)
								return (
									<Fragment key={stage.key}>
										{index > 0 && <span className={`of-stage-arrow ${stageState[STAGES[index - 1].key].status === 'success' ? 'of-stage-arrow-active' : ''}`} />}
										<Tooltip title={`${stage.label} \u2014 ${stageStatusText(stage.key, status)}`}>
											<div className={`of-stage of-stage-${status}`}>
												<div className="of-stage-node">{renderStageNode(status, index + 1)}</div>
												<div className="of-stage-label">{stage.short}</div>
											</div>
										</Tooltip>
									</Fragment>
								)
							})
						}
					</div>
				</div>

				<div className="of-body">
					<div className="of-section">
						<div className="of-section-head">
							<span className={`of-step ${selectedNumber ? 'of-step-done' : ''}`}>
								{selectedNumber ? <CheckCircleIcon style={{ fontSize: 20 }} /> : '#'}
							</span>
							<div>
								<h3>Select Optimization Number</h3>
								<p>{selectedNumber ? `Selected ${selectedNumber.optimizationNumber}` : 'Pick a number to use for this optimization'}</p>
							</div>
							<Tooltip title="Refresh">
								<IconButton size="small" onClick={fetchAvailableNumbers}><RefreshIcon /></IconButton>
							</Tooltip>
						</div>
						<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
							{
								selectedNumber
									? <Chip label={selectedNumber.optimizationNumber} color="primary" icon={<CheckCircleIcon />} />
									: availableNumbers.map(item => (
										<Chip
											key={item.number}
											label={item.optimizationNumber}
											variant="outlined"
											clickable
											disabled={reserving}
											onClick={() => handleSelectNumber(item)}
										/>
									))
							}
						</div>
					</div>

					{/* ---------- file uploads ---------- */}
					<div className="of-section">
						{
							creating &&
							<div className="of-section-loader">
								<CircularProgress size={34} thickness={5} style={{ color: '#f47d44' }} />
								<span>{busyText || 'Working\u2026'}</span>
							</div>
						}

						{
							failedStage &&
							<div className="of-failed">
								<ErrorOutlineIcon className="of-failed-icon" />
								<div className="of-created-text">
									<span className="of-created-title">
										{STAGES.find(stage => stage.key === failedStage)?.label} failed
									</span>
									<span className="of-created-sub">{stageState[failedStage]?.message}</span>
								</div>
								<Button
									className="of-btn of-btn-outline"
									size="small"
									startIcon={<RefreshIcon />}
									disabled={creating}
									onClick={() => runPipeline(failedStage)}
								>
									Resume from this step
								</Button>
							</div>
						}

						<div className="of-section-head">
							<span className={`of-step ${allFilesUploaded ? 'of-step-done' : ''}`}>
								{createdOptimisation ? <CheckCircleIcon style={{ fontSize: 20 }} /> : '1'}
							</span>
							<div>
								<h3>Upload Optimization Files</h3>
								<p>All {FILE_FIELDS.length} files are required before the BOM can be segregated</p>
							</div>
							<div className="of-progress">
								<div className="of-progress-track"><div className="of-progress-fill" style={{ width: `${(uploadedCount / FILE_FIELDS.length) * 100}%` }} /></div>
								<span className="of-progress-text">{uploadedCount} of {FILE_FIELDS.length} uploaded</span>
							</div>
						</div>

						{
							createdOptimisation &&
							<div className="of-created">
								<CheckCircleIcon className="of-created-icon" />
								<div className="of-created-text">
									<span className="of-created-title">Optimization Created</span>
									<span className="of-created-sub">
										Saved against {createdOptimisation.projects ? createdOptimisation.projects.length : 0} project(s)
										{createdOptimisation.projectNumbers && createdOptimisation.projectNumbers.length ? ` \u2014 ${createdOptimisation.projectNumbers.join(', ')}` : ''}
									</span>
								</div>
								<span className="of-created-number">{createdOptimisation.optimizationNumber}</span>
								<Tooltip title="View optimization files">
									<Link to="/optimisation_files/view" target="_blank" rel="noopener noreferrer">
										<IconButton className="of-created-eye" size="small"><VisibilityOutlinedIcon /></IconButton>
									</Link>
								</Tooltip>
							</div>
						}

						{
							bomsCreated.length > 0 &&
							<div className="of-created">
								<CheckCircleIcon className="of-created-icon" />
								<div className="of-created-text">
									<span className="of-created-title">BOM Files Created</span>
									<span className="of-created-sub">
										{bomsCreated.length} project(s) &mdash; {bomsCreated.reduce((total, row) => total + (row.totalItems || 0), 0)} BOM items in total
									</span>
								</div>
								<span className="of-created-number">
									{
										uploadResults.length > 0
											? `${uploadResults.filter(row => row.uploaded).length}/${uploadResults.length} Uploaded`
											: `${bomsCreated.length} BOM`
									}
								</span>
								<Tooltip title="View BOM file creation">
									<IconButton className="of-created-eye" size="small" onClick={() => setBomDialogOpen(true)}>
										<VisibilityOutlinedIcon />
									</IconButton>
								</Tooltip>
							</div>
						}

						<div className="of-upload-grid">
							{
								FILE_FIELDS.map(field => {
									const state = uploadedFiles[field.key]
									return (
										<div className={`of-upload-card of-upload-${state.status}`} key={field.key}>
											<div className="of-upload-top">
												<div>
													<div className="of-upload-title">{field.label}</div>
													<div className="of-upload-hint">{field.hint}</div>
												</div>
												{renderFileStatus(state)}
											</div>

											<label className="of-file-input">
												<input
													accept={field.accept}
													style={{ display: 'none' }}
													type="file"
													onChange={(e) => handleChooseFile(e, field.key)}
												/>
												<span className={`of-file-name ${state.fileName ? '' : 'of-file-name-empty'}`}>{state.fileName || 'No file chosen'}</span>
												<Button
													className="of-btn of-btn-outline"
													component="span"
													size="small"
													startIcon={state.status === 'success' ? <RefreshIcon /> : <CloudUploadOutlinedIcon />}
												>
													{state.status === 'success' ? 'Replace' : 'Browse'}
												</Button>
											</label>
											<div className="of-upload-accept">Accepted: {field.accept}</div>
										</div>
									)
								})
							}
						</div>
					</div>
				</div>

				{/* ---------- footer ---------- */}
				<div className="of-footer">
					<span className="of-hint">
						{
							failedStage
								? `Stopped at ${STAGES.find(stage => stage.key === failedStage)?.label}. Fix the cause and resume, or reset to start again.`
								: allStagesDone
									? `Optimization ${createdOptimisation?.optimizationNumber} completed through BOM upload. Use Reset to start a new one.`
									: creating
										? busyText
										: allFilesUploaded
											? `All ${FILE_FIELDS.length} files uploaded. One click runs optimization, segregation, BOM files and auto upload.`
											: `The process unlocks after all ${FILE_FIELDS.length} files upload successfully`
						}
					</span>
					<div className="of-footer-actions">
						{
							bomUploadDone &&
							<Tooltip title={segregatedFileLink ? 'Download the segregated BOM saved on this optimization' : 'Download the segregated BOM'}>
								<Button
									className="of-btn of-btn-outline"
									startIcon={<GetAppIcon />}
									disabled={creating}
									onClick={handleDownloadSegregatedBom}
								>
									Download Segregated BOM
								</Button>
							</Tooltip>
						}
						<Button className="of-btn of-btn-ghost" disabled={creating} onClick={resetForm}>Reset</Button>
						<Button
							className={`of-btn of-btn-primary ${(!allFilesUploaded || !selectedNumber || creating || createdOptimisation) ? 'of-btn-disabled' : ''}`}
							variant="contained"
							disableElevation
							disabled={!allFilesUploaded || !selectedNumber || creating || Boolean(createdOptimisation)}
							onClick={handleCreateBomSegregation}
							startIcon={creating ? <CircularProgress size={15} thickness={6} style={{ color: '#fff' }} /> : null}
						>
							{
								creating
									? `Running ${completedStages + 1} of ${STAGES.length}\u2026`
									: allStagesDone ? 'Completed' : 'Start Optimization Process'
							}
						</Button>
					</div>
				</div>
			</div>

			{/* ---------- bom file creation, same data as the api response ---------- */}
			<Dialog open={bomDialogOpen} onClose={() => setBomDialogOpen(false)} maxWidth="lg" fullWidth>
				<DialogTitle>BOM File Creation</DialogTitle>
				<DialogContent dividers>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell><b>Project</b></TableCell>
								<TableCell align="center"><b>BOM File Creation</b></TableCell>
								<TableCell align="center"><b>View BOM</b></TableCell>
								<TableCell align="center"><b>Total Items</b></TableCell>
								<TableCell align="center"><b>Status</b></TableCell>
								<TableCell align="center"><b>Project Upload Status</b></TableCell>
								<TableCell align="center"><b>Auto Approve Status</b></TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{
								bomsCreated.map(row => (
									<Fragment key={row.projectNumber}>
										<TableRow>
											<TableCell>{row.projectNumber}</TableCell>
											<TableCell align="center"><CheckCircleIcon style={{ color: '#23a566', fontSize: 22 }} /></TableCell>
											<TableCell align="center">
												<Button
													size="small"
													variant="outlined"
													startIcon={<VisibilityOutlinedIcon />}
													onClick={() => setViewBomProject(viewBomProject === row.projectNumber ? '' : row.projectNumber)}
												>
													{viewBomProject === row.projectNumber ? 'Hide' : 'View'}
												</Button>
											</TableCell>
											<TableCell align="center">{row.totalItems}</TableCell>
											<TableCell align="center">BOM Created</TableCell>
											<TableCell align="center">
												{
													uploadStatusFor(row.projectNumber)
														? <Tooltip title={uploadStatusFor(row.projectNumber).message || ''}>
															<span style={{ color: uploadStatusFor(row.projectNumber).uploaded ? '#23a566' : '#d64550', fontWeight: 600 }}>
																{uploadStatusFor(row.projectNumber).status}
															</span>
														</Tooltip>
														: '-'
												}
											</TableCell>
											<TableCell align="center">
												{
													approveStatusFor(row.projectNumber)
														? approveStatusFor(row.projectNumber).approved
															? <span style={{ color: '#23a566', fontWeight: 600 }}>Approved</span>
															: <span style={{ color: '#d64550', fontWeight: 600 }}>This is not approve due to unmeet conitions</span>
														: '-'
												}
											</TableCell>
										</TableRow>
										{
											viewBomProject === row.projectNumber &&
											<TableRow>
												<TableCell colSpan={7} style={{ background: '#fafbfe' }}>
													<Table size="small">
														<TableHead>
															<TableRow>
																<TableCell><b>Room</b></TableCell>
																<TableCell><b>Scope</b></TableCell>
																<TableCell><b>Sub Category</b></TableCell>
																<TableCell><b>Material</b></TableCell>
																<TableCell align="center"><b>UOM</b></TableCell>
																<TableCell align="center"><b>Qty</b></TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{
																(row.bomItems || []).map((item, index) => (
																	<TableRow key={`${row.projectNumber}-${index}`}>
																		<TableCell>{item["Select Room"]}</TableCell>
																		<TableCell>{item["Select Scope"]}</TableCell>
																		<TableCell>{item["Scope Sub category"]}</TableCell>
																		<TableCell>{item["Select Material"]}</TableCell>
																		<TableCell align="center">{item["UOM"]}</TableCell>
																		<TableCell align="center">{item["Qty"]}</TableCell>
																	</TableRow>
																))
															}
														</TableBody>
													</Table>
												</TableCell>
											</TableRow>
										}
									</Fragment>
								))
							}
						</TableBody>
					</Table>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setBomDialogOpen(false)}>Close</Button>
				</DialogActions>
			</Dialog>
		</div>
	)
}

export default connect(null, { setLoading, setSnackInfo })(Index)
